import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ptBR } from 'date-fns/locale';
import { addDays, format } from 'date-fns';
import axios from 'axios';
import {
  User, Phone, Mail, ChevronRight, ChevronLeft,
  CheckCircle, Clock, Scissors, Calendar, AlertCircle, Loader
} from 'lucide-react';
import './BookingPage.css';

const STEPS = ['Profissional', 'Serviço', 'Data & Hora', 'Seus Dados', 'Confirmação'];

export default function BookingPage() {
  const [step, setStep] = useState(0);
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [selectedStylist, setSelectedStylist] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const preselectId = searchParams.get('stylist');
    axios.get('/api/stylists').then(r => {
      setStylists(r.data);
      if (preselectId) {
        const found = r.data.find(s => s.id === parseInt(preselectId));
        if (found) { setSelectedStylist(found); setStep(1); }
      }
    }).catch(() => {});
    axios.get('/api/services').then(r => setServices(r.data)).catch(() => {});
  }, [searchParams]);

  useEffect(() => {
    if (selectedStylist && selectedDate && selectedService) {
      setLoadingSlots(true);
      setSelectedTime(null);
      axios.get('/api/availability', {
        params: {
          stylist_id: selectedStylist.id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          service_id: selectedService.id,
        }
      }).then(r => {
        setAvailableSlots(r.data.available);
        setLoadingSlots(false);
      }).catch(() => setLoadingSlots(false));
    }
  }, [selectedStylist, selectedDate, selectedService]);

  const handleSubmit = async () => {
    if (!clientName.trim() || !clientPhone.trim()) {
      setError('Por favor, preencha seu nome e telefone.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post('/api/bookings', {
        client_name: clientName,
        client_phone: clientPhone,
        client_email: clientEmail,
        stylist_id: selectedStylist.id,
        service_id: selectedService.id,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedTime,
      });
      navigate('/confirmacao', { state: { booking: res.data.booking } });
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao confirmar reserva. Tente novamente.');
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!selectedStylist;
    if (step === 1) return !!selectedService;
    if (step === 2) return !!selectedDate && !!selectedTime;
    if (step === 3) return clientName.trim() && clientPhone.trim();
    return true;
  };

  const stylistInitials = (name) =>
    name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const stylistColors = [
    'linear-gradient(135deg, #c9a96e, #e8c5cb)',
    'linear-gradient(135deg, #a07840, #c9a96e)',
    'linear-gradient(135deg, #e8c5cb, #c9a96e)',
  ];

  return (
    <div className="booking-page">
      <div className="booking-hero">
        <h1>Agendar Horário</h1>
        <p>Escolha sua profissional, serviço e horário favorito</p>
      </div>

      <div className="booking-container">
        {/* Progress Steps */}
        <div className="booking-steps">
          {STEPS.map((s, i) => (
            <div key={i} className={`booking-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
              <div className="step-circle">
                {i < step ? <CheckCircle size={16} /> : <span>{i + 1}</span>}
              </div>
              <span className="step-label">{s}</span>
              {i < STEPS.length - 1 && <div className="step-line" />}
            </div>
          ))}
        </div>

        <div className="booking-content">
          {/* STEP 0: Choose Stylist */}
          {step === 0 && (
            <div className="step-panel">
              <h2>Escolha sua Profissional</h2>
              <p className="step-desc">Selecione a especialista que irá te atender</p>
              <div className="stylist-pick-grid">
                {stylists.map((s, idx) => (
                  <button
                    key={s.id}
                    className={`stylist-pick-card ${selectedStylist?.id === s.id ? 'selected' : ''}`}
                    onClick={() => setSelectedStylist(s)}
                  >
                    <div className="pick-avatar" style={{ background: stylistColors[idx % stylistColors.length], overflow: 'hidden' }}>
                      {s.photo ? (
                        <img
                          src={`/uploads/${s.photo}`}
                          alt={s.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <span style={{ display: s.photo ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                        {stylistInitials(s.name)}
                      </span>
                    </div>
                    <div className="pick-info">
                      <strong>{s.name}</strong>
                      <span>{s.specialty}</span>
                    </div>
                    {selectedStylist?.id === s.id && (
                      <CheckCircle size={20} className="pick-check" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1: Choose Service */}
          {step === 1 && (
            <div className="step-panel">
              <h2>Escolha o Serviço</h2>
              <p className="step-desc">O que você gostaria de fazer hoje?</p>
              <div className="service-pick-grid">
                {services.map(s => (
                  <button
                    key={s.id}
                    className={`service-pick-card ${selectedService?.id === s.id ? 'selected' : ''}`}
                    onClick={() => setSelectedService(s)}
                  >
                    <div className="service-pick-top">
                      <span className="service-pick-name">{s.name}</span>
                      {selectedService?.id === s.id && <CheckCircle size={18} className="pick-check" />}
                    </div>
                    <p className="service-pick-desc">{s.description}</p>
                    <div className="service-pick-meta">
                      <span><Clock size={13} /> {s.duration_minutes} min</span>
                      <span className="service-pick-price">R$ {s.price.toFixed(2).replace('.', ',')}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Choose Date & Time */}
          {step === 2 && (
            <div className="step-panel">
              <h2>Escolha Data & Horário</h2>
              <p className="step-desc">Veja os horários disponíveis para sua profissional</p>
              <div className="datetime-layout">
                <div className="date-picker-wrap">
                  <label className="date-label">
                    <Calendar size={15} /> Selecione a data
                  </label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={setSelectedDate}
                    inline
                    locale={ptBR}
                    minDate={addDays(new Date(), 1)}
                    filterDate={date => date.getDay() !== 0}
                    dateFormat="dd/MM/yyyy"
                  />
                </div>

                <div className="slots-wrap">
                  <label className="date-label">
                    <Clock size={15} /> Horários disponíveis
                  </label>
                  {!selectedDate && (
                    <div className="slots-placeholder">
                      <Calendar size={36} strokeWidth={1} />
                      <p>Selecione uma data para ver os horários</p>
                    </div>
                  )}
                  {selectedDate && loadingSlots && (
                    <div className="slots-loading">
                      <Loader size={24} className="spin" />
                      <p>Verificando disponibilidade...</p>
                    </div>
                  )}
                  {selectedDate && !loadingSlots && availableSlots.length === 0 && (
                    <div className="slots-empty">
                      <AlertCircle size={36} strokeWidth={1} />
                      <p>Nenhum horário disponível nesta data. Escolha outro dia.</p>
                    </div>
                  )}
                  {selectedDate && !loadingSlots && availableSlots.length > 0 && (
                    <div className="slots-grid">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          className={`slot-btn ${selectedTime === slot ? 'selected' : ''}`}
                          onClick={() => setSelectedTime(slot)}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Client Data */}
          {step === 3 && (
            <div className="step-panel">
              <h2>Seus Dados</h2>
              <p className="step-desc">Preencha seus dados para confirmar a reserva</p>
              <div className="client-form">
                <div className="form-group">
                  <label><User size={15} /> Nome completo *</label>
                  <input
                    type="text"
                    placeholder="Seu nome"
                    value={clientName}
                    onChange={e => setClientName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label><Phone size={15} /> Telefone / WhatsApp *</label>
                  <input
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={clientPhone}
                    onChange={e => setClientPhone(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label><Mail size={15} /> E-mail (opcional)</label>
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={clientEmail}
                    onChange={e => setClientEmail(e.target.value)}
                  />
                </div>
                {error && (
                  <div className="form-error">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 4: Summary */}
          {step === 4 && (
            <div className="step-panel">
              <h2>Confirmar Reserva</h2>
              <p className="step-desc">Revise os detalhes antes de confirmar</p>
              <div className="summary-card">
                <div className="summary-row">
                  <span className="summary-label"><User size={15} /> Profissional</span>
                  <span className="summary-value">{selectedStylist?.name}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label"><Scissors size={15} /> Serviço</span>
                  <span className="summary-value">{selectedService?.name}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label"><Calendar size={15} /> Data</span>
                  <span className="summary-value">
                    {selectedDate && format(selectedDate, "dd/MM/yyyy")}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label"><Clock size={15} /> Horário</span>
                  <span className="summary-value">{selectedTime}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label"><User size={15} /> Cliente</span>
                  <span className="summary-value">{clientName}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label"><Phone size={15} /> Telefone</span>
                  <span className="summary-value">{clientPhone}</span>
                </div>
                <div className="summary-divider" />
                <div className="summary-row summary-total">
                  <span className="summary-label">Valor do serviço</span>
                  <span className="summary-price">
                    R$ {selectedService?.price.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
              {error && (
                <div className="form-error">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Navigation */}
          <div className="booking-nav">
            {step > 0 && (
              <button className="btn-back" onClick={() => { setStep(s => s - 1); setError(''); }}>
                <ChevronLeft size={18} /> Voltar
              </button>
            )}
            <div style={{ flex: 1 }} />
            {step < 4 && (
              <button
                className="btn-next"
                disabled={!canProceed()}
                onClick={() => setStep(s => s + 1)}
              >
                Próximo <ChevronRight size={18} />
              </button>
            )}
            {step === 4 && (
              <button
                className="btn-confirm"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <><Loader size={18} className="spin" /> Confirmando...</>
                ) : (
                  <><CheckCircle size={18} /> Confirmar Reserva</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
